import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Button, Container, IconButton } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../../api/biApi";
import { pageRoutes } from "../../../../../routes";
import GeneralPageHeader from "../../../../common/GeneralPageHeader";
import WorkbookDetailsContainer from "../../common/WorkbookDetailsContainer";
import WorkbookFilters from "../../common/WorkbookFilters";
import DeleteTemplatesDialog from "../../dialogs/delete-templates/DeleteTemplatesDialog";
import WorkbookTemplateDetailsMeta from "./WorkbookTemplateDetailsMeta";
import WorkbookTemplateDetailsPageTitle from "./WorkbookTemplateDetailsPageTitle";
import WorkbookTemplateDetailsReports from "./WorkbookTemplateDetailsReports";

const WorkbookTemplateDetailsPage = () => {
  usePageTitle("Workbook Template");

  const { templateId } = useParams();
  const navigate = useNavigate();
  const [openDeleteTemplateDialog, setOpenDeleteTemplateDialog] = useState(false);

  const getWorkbookTemplate = useCallback(
    (abortSignal?: AbortSignal) => biApi.getWorkbookTemplate(templateId ?? "", abortSignal),
    [templateId]
  );

  const [workbookTemplate, fetchError, { isFetching }] = useFetch(getWorkbookTemplate);

  const handleBack = () => {
    navigate(`../../${pageRoutes.workbookRunner}?tab=templates`);
  };

  if (fetchError) {
    logError(fetchError, "[WorkbookTemplateDetailsPage] getWorkbookTemplate");
    return <DataLoadingFailed title="Failed to load workbook template" />;
  }

  if (workbookTemplate === undefined || isFetching) {
    return <InlineLoader />;
  }

  return (
    <>
      <GeneralPageHeader
        BackButton={
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        }
        TitleComponent={<WorkbookTemplateDetailsPageTitle workbookTemplate={workbookTemplate} />}
      >
        <AuthorizedBox permissions={["ManageReports"]}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => setOpenDeleteTemplateDialog(true)}
          >
            Delete
          </Button>
        </AuthorizedBox>
      </GeneralPageHeader>
      <Container maxWidth="xl" disableGutters sx={{ height: "100%", display: "flex" }}>
        <WorkbookTemplateDetailsReports workbookTemplate={workbookTemplate} />
        <WorkbookDetailsContainer>
          <WorkbookTemplateDetailsMeta workbookTemplate={workbookTemplate} />
          <WorkbookFilters workbookFilters={workbookTemplate.workbookFilters} />
        </WorkbookDetailsContainer>
      </Container>
      <AuthorizedBox permissions={["ManageReports"]}>
        {openDeleteTemplateDialog && (
          <DeleteTemplatesDialog
            templates={[workbookTemplate]}
            onClose={() => setOpenDeleteTemplateDialog(false)}
            onDeleted={() => {
              setOpenDeleteTemplateDialog(false);
              handleBack();
            }}
          />
        )}
      </AuthorizedBox>
    </>
  );
};

export default WorkbookTemplateDetailsPage;
